import React, { useMemo, useState } from "react";
import { Check, Copy, Facebook, Share2 } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

const L = {
  title: { nl: "Deel deze pagina", de: "Diese Seite teilen", en: "Share this page" },
  whatsapp: { nl: "Deel via WhatsApp", de: "Über WhatsApp teilen", en: "Share via WhatsApp" },
  facebook: { nl: "Deel via Facebook", de: "Über Facebook teilen", en: "Share via Facebook" },
  copy: { nl: "Kopieer link", de: "Link kopieren", en: "Copy link" },
  copied: { nl: "Gekopieerd!", de: "Kopiert!", en: "Copied!" },
  share: { nl: "Delen", de: "Teilen", en: "Share" },
  op: { nl: "op", de: "auf", en: "on" },
};

// Simpel WhatsApp-groen icoon (lucide heeft geen officieel WhatsApp-logo).
const WhatsAppIcon = ({ className = "w-4 h-4" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M20.52 3.48A11.86 11.86 0 0 0 12.05 0C5.5 0 .2 5.3.2 11.85c0 2.09.55 4.12 1.6 5.92L0 24l6.4-1.68a11.83 11.83 0 0 0 5.65 1.44h.01c6.55 0 11.85-5.3 11.85-11.85 0-3.17-1.23-6.15-3.39-8.43ZM12.06 21.5h-.01a9.62 9.62 0 0 1-4.9-1.34l-.35-.21-3.8 1 1.02-3.7-.23-.38a9.6 9.6 0 0 1-1.47-5.1c0-5.3 4.32-9.62 9.63-9.62 2.57 0 4.98 1 6.8 2.82a9.55 9.55 0 0 1 2.82 6.8c0 5.32-4.32 9.63-9.63 9.63Zm5.55-7.2c-.3-.15-1.79-.88-2.07-.98-.28-.1-.48-.15-.68.15-.2.3-.78.98-.96 1.18-.18.2-.35.22-.65.07-.3-.15-1.28-.47-2.44-1.5-.9-.8-1.5-1.8-1.68-2.1-.18-.3-.02-.46.13-.61.13-.14.3-.35.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.08-.15-.68-1.64-.93-2.24-.25-.6-.5-.52-.68-.53l-.58-.01c-.2 0-.53.08-.8.38s-1.06 1.04-1.06 2.53c0 1.5 1.08 2.94 1.23 3.14.15.2 2.12 3.25 5.14 4.55.72.31 1.28.5 1.72.64.72.23 1.38.2 1.9.12.58-.09 1.79-.73 2.04-1.44.25-.72.25-1.32.18-1.45-.08-.13-.28-.2-.58-.35Z" />
  </svg>
);

const buildShareText = (name, url, lang) =>
  `${name} ${L.op[lang] || L.op.nl} Lemmer Leeft — ${url}`;

// Baseer op de frontend REACT_APP_BACKEND_URL zodat crawlers het OG-endpoint kunnen fetchen
// (zowel op preview als op productie klopt de host dan automatisch).
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export const ShareButtons = ({ name = "Deze vermelding", slug = "" }) => {
  const { lang } = useLang();
  const [copied, setCopied] = useState(false);

  // Echte SPA-URL (voor kopie en native share): volledige, absolute URL incl. taal-prefix.
  const pageUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return window.location.href;
  }, []);

  // Server-side OG-endpoint. WhatsApp/Facebook/Twitter/LinkedIn crawlers fetchen deze URL
  // en lezen de OG-tags. Browsers worden onmiddellijk (meta refresh + JS) doorgestuurd naar
  // de echte /bedrijf/<slug> SPA-pagina. Zo blijft de UX identiek.
  const crawlerUrl = useMemo(() => {
    if (!slug || !BACKEND_URL) return pageUrl;
    const langPart = lang === "nl" ? "" : `/${lang}`;
    return `${BACKEND_URL}/api/share${langPart}/bedrijf/${slug}`;
  }, [slug, lang, pageUrl]);

  const shareText = buildShareText(name, crawlerUrl, lang);
  const waHref = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(crawlerUrl)}`;

  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const onNativeShare = async () => {
    try {
      await navigator.share({
        title: `${name} · Lemmer Leeft`,
        text: `${name} ${L.op[lang] || L.op.nl} Lemmer Leeft`,
        url: pageUrl,
      });
    } catch {
      // User annuleerde of niet ondersteund — geen actie.
    }
  };

  const onCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(pageUrl);
      } else {
        // Legacy fallback
        const ta = document.createElement("textarea");
        ta.value = pageUrl;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // stil falen — link kopiëren mag nooit crashen
    }
  };

  const iconBtn =
    "inline-flex items-center justify-center w-9 h-9 rounded-full border border-[#E4EEF5] bg-white text-[#173A5E] hover:bg-[#F4F8FB] hover:border-[#6BC0E8] transition shadow-sm";

  return (
    <div
      data-testid="share-buttons"
      className="mt-6 mb-2 flex flex-wrap items-center gap-3"
    >
      <span className="text-xs font-[Poppins] font-semibold uppercase tracking-[0.14em] text-[#5A7594]">
        {L.title[lang] || L.title.nl}
      </span>

      <div className="flex items-center gap-2">
        {canNativeShare && (
          <button
            type="button"
            onClick={onNativeShare}
            data-testid="share-btn-native"
            aria-label={L.share[lang] || L.share.nl}
            title={L.share[lang] || L.share.nl}
            className={`${iconBtn} sm:hidden`}
          >
            <Share2 className="w-4 h-4" />
          </button>
        )}

        <a
          href={waHref}
          target="_blank"
          rel="noreferrer noopener"
          data-testid="share-btn-whatsapp"
          aria-label={L.whatsapp[lang] || L.whatsapp.nl}
          title={L.whatsapp[lang] || L.whatsapp.nl}
          className={`${iconBtn} hover:text-[#25D366]`}
        >
          <WhatsAppIcon className="w-4 h-4" />
        </a>

        <a
          href={fbHref}
          target="_blank"
          rel="noreferrer noopener"
          data-testid="share-btn-facebook"
          aria-label={L.facebook[lang] || L.facebook.nl}
          title={L.facebook[lang] || L.facebook.nl}
          className={`${iconBtn} hover:text-[#1877F2]`}
        >
          <Facebook className="w-4 h-4" />
        </a>

        <button
          type="button"
          onClick={onCopy}
          data-testid="share-btn-copy"
          aria-label={L.copy[lang] || L.copy.nl}
          title={L.copy[lang] || L.copy.nl}
          className={`${iconBtn} ${copied ? "text-[#2E9CD6] border-[#6BC0E8]" : ""}`}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>

        <span
          data-testid="share-copied-feedback"
          role="status"
          aria-live="polite"
          className={`text-xs font-[Poppins] font-semibold text-[#2E9CD6] transition-opacity duration-200 ${
            copied ? "opacity-100" : "opacity-0"
          }`}
        >
          {L.copied[lang] || L.copied.nl}
        </span>
      </div>
    </div>
  );
};

export default ShareButtons;
